import Pagination from '../../components/Common/Pagination';
import { TableLoader, Spinner } from '../../components/Common/Loader';
import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { getBrands, createBrand, updateBrand } from '../../api/productMasters';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface Brand {
  id: number;
  name: string;
  pictureUrl?: string;
  erpId: string;
  sequenceNo: string;
  status: 'Active' | 'Inactive';
  company?: string;
  created?: string;
  modified?: string;
}

export const BrandsList: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [view, setView] = useState<'list' | 'add' | 'edit' | 'view'>('list');
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [viewingBrand, setViewingBrand] = useState<Brand | null>(null);

  // Fetch Brands from API
  const fetchBrandsData = async () => {
    try {
      setLoading(true);
      const res = await getBrands();
      if (res && (res.status === 'success' || res.success) && res.data) {
        const rawList = Array.isArray(res.data)
          ? res.data
          : ((res.data as any)?.data || (res.data as any)?.brands || []);

        if (Array.isArray(rawList)) {
          const mapped: Brand[] = rawList.map((item: any) => ({
            id: item.id,
            name: item.name || item.brandName || item.brand_name || '',
            pictureUrl: item.logoUrl || item.pictureUrl || '',
            erpId: item.sapCode || item.createdBy || item.erpId || '-',
            sequenceNo: String(item.id),
            status: (item.status === 1 || item.status === '1' || item.status === 'Active') ? 'Active' : 'Inactive',
            company: 'Gowardhan',
            created: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-',
            modified: item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '0'
          }));
          setBrands(mapped);
        }
      }
    } catch (err) {
      console.error('Error fetching brands list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandsData();
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    pictureUrl: '',
    erpId: '',
    sequenceNo: '',
    status: 'Active' as 'Active' | 'Inactive',
  });

  // Filtering Logic
  const filteredBrands = useMemo(() => {
    return brands.filter((brand) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          brand.name.toLowerCase().includes(query) ||
          brand.erpId.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [brands, searchQuery]);

  // Paginated Brands
  const paginatedBrands = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredBrands.slice(startIndex, startIndex + entriesLimit);
  }, [filteredBrands, currentPage, entriesLimit]);

  const handleOpenAdd = () => {
    setEditingBrand(null);
    setFormData({ name: '', pictureUrl: '', erpId: '', sequenceNo: '', status: 'Active' });
    setView('add');
  };

  const handleOpenEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      pictureUrl: brand.pictureUrl || '',
      erpId: brand.erpId === '-' ? '' : brand.erpId,
      sequenceNo: brand.sequenceNo,
      status: brand.status || 'Active',
    });
    setView('edit');
  };
  const handlerOpenView = (brand: Brand) => {
    setViewingBrand(brand);
    setView('view');
  };

  const _handleDeleteBrand = (brandId: number) => {
    if (window.confirm('Are you sure you want to delete this brand?')) {
      setBrands(brands.filter(b => b.id !== brandId));
      setView('list');
    }
  };
  void _handleDeleteBrand;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Name is required.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name,
        brandName: formData.name,
        sapCode: formData.erpId || undefined,
        logoUrl: formData.pictureUrl || undefined,
        status: formData.status === 'Active' ? 1 : 0,
      };

      if (editingBrand) {
        // Update Brand: PUT /api/v1/erp/products/brands/:id
        await updateBrand(editingBrand.id, payload);
      } else {
        // Create Brand: POST /api/v1/erp/products/brands
        await createBrand(payload);
      }

      await fetchBrandsData();
      setView('list');
    } catch (err: any) {
      console.error('Error saving brand:', err);
      alert(err.message || 'Failed to save brand. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (view === 'view' && viewingBrand) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button 
              type="button"
              onClick={() => setView('list')}
              className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
              View Brand
            </h1>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleOpenAdd}
              className="go_erp-bg text-white text-xs font-bold py-2.5 px-4 rounded-xl flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Add Brand
            </button>
            <button
              onClick={() => handleOpenEdit(viewingBrand)}
              className="bg-[#F9C327] hover:bg-[#E5B21E] text-white text-xs font-bold py-2.5 px-4 rounded-xl flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Brand
            </button>
            {/* <button
              onClick={() => handleDeleteBrand(viewingBrand.id)}
              className="bg-[#4E342E] hover:bg-[#3E2723] text-white text-xs font-bold py-2.5 px-4 rounded-xl flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Brand
            </button> */}
          </div>
        </div>

        {/* Details Card */}
        <div className="bg-white rounded-3xl p-6 space-y-4">
          <h4 className="text-sm font-bold text-slate-800 m-0">Brand Details</h4>
          <hr className="border-[#ECECF2] m-0" />
          
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-6 pt-2">
            <div>
              <div className="text-[11px] font-semibold text-slate-400 mb-1">Brand ID</div>
              <div className="text-xs font-semibold text-slate-800">
                {String(viewingBrand.id).padStart(2, '0')}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-slate-400 mb-1">Name</div>
              <div className="text-xs font-semibold text-slate-800">{viewingBrand.name}</div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-slate-400 mb-1">Company</div>
              <div className="text-xs font-semibold text-slate-800">
                {viewingBrand.company || `${viewingBrand.name} Milk`}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-slate-400 mb-1">Created</div>
              <div className="text-xs font-semibold text-slate-800">{viewingBrand.created || '-'}</div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-slate-400 mb-1">Modified</div>
              <div className="text-xs font-semibold text-slate-800">{viewingBrand.modified || '0'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button 
            type="button"
            onClick={() => setView('list')}
            className="w-7 h-7 rounded-full go_erp-bg transition-all flex items-center justify-center text-white cursor-pointer" title="Go Back"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h3 className="text-xl font-bold go_erp-text tracking-wide">
            {view === 'edit' ? 'Edit Brand' : 'Add Brand'}
          </h3>
        </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Name */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Name</label>
              <input 
                type="text" 
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Picture URL */}
            {/* <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Picture URL</label>
              <input 
                type="text" 
                value={formData.pictureUrl}
                onChange={(e) => setFormData({...formData, pictureUrl: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div> */}

            {/* ERP ID */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">ERP/SAP ID</label>
              <input 
                type="text" 
                value={formData.erpId}
                onChange={(e) => setFormData({...formData, erpId: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div>

            {/* Status Dropdown */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Status</label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                  className="w-full bg-[#F5F5F5] border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 appearance-none cursor-pointer"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Sequence No */}
            {/* <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Sequence No</label>
              <input 
                type="text" 
                value={formData.sequenceNo}
                onChange={(e) => setFormData({...formData, sequenceNo: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800" 
              />
            </div> */}

            {/* Brand Image Upload */}
            {/* <div className="col-span-1 md:col-span-2">
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Brand Image</label>
              <div className="flex items-center gap-3">
                <div className="flex flex-1 items-center border border-[#828282] rounded-2xl overflow-hidden bg-white">
                  <button 
                    type="button" 
                    className="go_erp-bg text-white text-xs font-bold py-2.5 px-4 transition-colors"
                  >
                    Upload
                  </button>
                  <input 
                    type="text" 
                    readOnly
                    placeholder="Choose file" 
                    className="flex-1 bg-transparent px-3 text-xs text-slate-500 outline-none cursor-default font-semibold"
                  />
                  <button 
                    type="button" 
                    className="bg-[#FFF8E1] text-slate-700 hover:bg-[#FFF3CD] text-xs font-bold py-2.5 px-4 transition-colors"
                  >
                    Browse
                  </button>
                </div>
                <button 
                  type="button" 
                  className="go_erp-bg text-white text-xs font-bold py-2.5 px-6 rounded-xl transition-colors cursor-pointer"
                >
                  Remove
                </button>
              </div>
            </div> */}
            {/* Submit Button */}
            <div className="flex justify-center pt-4 col-span-1 md:col-span-2">
              <button 
                type="submit" 
                disabled={submitting}
                className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting && <Spinner size="sm" color="text-white" />}
                {submitting ? 'Saving...' : 'Submit'}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  const handleExportCSV = () => {
    const headers = ['Brand ID', 'Name', 'ERP ID', 'Sequence No', 'Status'];
    const rows = filteredBrands.map((b) => [
      b.id,
      b.name,
      b.erpId,
      b.sequenceNo,
      b.status,
    ]);
    exportToCSV('brands_list', headers, rows);
  };

  return (
    <div className="space-y-6">
      {/* Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Brands
          </h1>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-1.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Brand
          </button>
          
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Table Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-slate-400 w-44 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFF8E1] ">
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Status</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {loading ? (
                <TableLoader colSpan={4} text="Loading..." />
              ) : paginatedBrands.length > 0 ? (
                paginatedBrands.map((brand) => (
                  <tr key={brand.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4 text-xs text-[#828282]">{brand.id}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{brand.name}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{brand.status}</td>
                    <td className="px-4 py-4 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button 
                            onClick={() => handlerOpenView(brand)}
                            className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                            title="View"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button
                            onClick={() => handleOpenEdit(brand)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                    No brands found matching filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredBrands.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>
    </div>
  );
};
export default BrandsList;
